import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { AddIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	Divider,
	FormControl,
	FormErrorMessage,
	Input,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import { FiFileText, FiGlobe, FiHash, FiMapPin, FiShield, FiUpload, FiUser } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";

import { addSecurityDepositAsync } from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { addSecurityDepositVerifier } from "../../utils/verifier";
import Dropzone from "../Dropzone/Dropzone";
import SecurityDepositRow from "../TableRows/SecurityDepositRow";

export default function SecurityDepositTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const initialPaymentInformation = {
		location: "custodyBank",
		custodian: "",
		documentNumber: "",
		propertyId: property.id,
		amount: "",
		currency: "ron",
		documents: [],
	};
	const dispatch = useDispatch();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [paymentInformation, setPaymentInformation] = useState(initialPaymentInformation);
	const [error, setError] = useState({});
	const toast = useToast();

	const onDocumentDrop = async (acceptedFiles) => {
		let documents = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			documents.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}
		setPaymentInformation({ ...paymentInformation, documents });
	};

	const addPayment = () => {
		const errors = addSecurityDepositVerifier(paymentInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: t("misc.error"),
				description: t("property.invalidInformation"),
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addSecurityDepositAsync(paymentInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: t("misc.error"),
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: t("misc.success"),
						description: t("property.securityDepositAdded"),
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onClose();
				setPaymentInformation(initialPaymentInformation);
			});
	};

	const handleChange = (e) => {
		setPaymentInformation({ ...paymentInformation, [e.target.name]: e.target.value });
	};

	const handleCloseModal = () => {
		onClose();
		setPaymentInformation(initialPaymentInformation);
		setError({});
	};

	const renderAddPaymentModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseModal}
			size="4xl"
			icon={FiShield}
			title={t("property.addSecurityDeposit")}
			subtitle={t("property.addSecurityDepositSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseModal} onConfirm={addPayment} confirmLabel={t("misc.save")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiShield} title={t("property.depositInformation")}>
						<VStack spacing={3} align="stretch">
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl isInvalid={error?.documentNumber}>
									<ModalFieldLabel icon={FiFileText}>{t("property.documentNumberLabel")}</ModalFieldLabel>
									<Input
										size="sm"
										onChange={handleChange}
										type="text"
										name="documentNumber"
										placeholder={t("property.documentNumberPlaceholder")}
										borderRadius="md"
									/>
									<FormErrorMessage>{error?.documentNumber}</FormErrorMessage>
								</FormControl>
								<FormControl isInvalid={error?.location}>
									<ModalFieldLabel icon={FiMapPin}>{t("property.locationLabel")}</ModalFieldLabel>
									<Select size="sm" onChange={handleChange} name="location" defaultValue="custodyBank" borderRadius="md">
										<option value="custodyBank">{t("types.custodyBank")}</option>
										<option value="custodyCash">{t("types.custodyCash")}</option>
										<option value="returned">{t("types.returned")}</option>
										<option value="returnedTenant">{t("types.returnedTenant")}</option>
										<option value="owner">{t("types.owner")}</option>
									</Select>
									<FormErrorMessage>{error?.location}</FormErrorMessage>
								</FormControl>
							</SimpleGrid>
							<SimpleGrid columns={2} spacing={4} w="full">
								<FormControl isInvalid={error?.amount}>
									<ModalFieldLabel icon={FiHash}>{t("property.amountLabel")}</ModalFieldLabel>
									<Input
										size="sm"
										type="number"
										name="amount"
										placeholder={t("property.amountPlaceholder")}
										value={paymentInformation.amount}
										onChange={handleChange}
										borderRadius="md"
									/>
									<FormErrorMessage>{error?.amount}</FormErrorMessage>
								</FormControl>
								<FormControl isInvalid={error?.currency}>
									<ModalFieldLabel icon={FiGlobe}>{t("property.currencyLabel")}</ModalFieldLabel>
									<Select
										size="sm"
										name="currency"
										placeholder={t("property.currencyPlaceholder")}
										value={paymentInformation.currency}
										onChange={handleChange}
										borderRadius="md"
									>
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
									<FormErrorMessage>{error?.currency}</FormErrorMessage>
								</FormControl>
							</SimpleGrid>
							<FormControl isInvalid={error?.custodian}>
								<ModalFieldLabel icon={FiUser}>{t("property.custodianLabel")}</ModalFieldLabel>
								<Input
									size="sm"
									type="text"
									name="custodian"
									placeholder={t("property.custodianPlaceholder")}
									onChange={handleChange}
									borderRadius="md"
								/>
								<FormErrorMessage>{error?.custodian}</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={error?.description}>
								<ModalFieldLabel icon={FiFileText}>{t("property.descriptionLabel")}</ModalFieldLabel>
								<Textarea
									size="sm"
									name="description"
									placeholder={t("property.descriptionPlaceholder")}
									onChange={handleChange}
									rows={3}
									resize="none"
									borderRadius="md"
								/>
								<FormErrorMessage>{error?.description}</FormErrorMessage>
							</FormControl>
						</VStack>
					</PropertyModalSection>
				</VStack>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiUpload} title={t("property.documentsLabel")}>
						<Dropzone
							onDrop={onDocumentDrop}
							accept={{
								"application/pdf": [".pdf"],
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
								"application/msword": [".doc"],
							}}
							description={t("property.uploadDocumentsDescription")}
						/>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Stack spacing={6}>
			<Box>
				<Button onClick={onOpen} colorScheme="blue" isDisabled={isArchived} leftIcon={<AddIcon />}>
					{t("property.addSecurityDeposit")}
				</Button>
			</Box>
			<Divider />
			{renderAddPaymentModal()}
			<Box overflowX="auto">
				<Table variant="simple" size="sm">
					<Thead>
						<Tr my=".8rem" pl="0px">
							<Th pl="0px">{t("table.addedDate")}</Th>
							<Th>{t("table.location")}</Th>
							<Th>{t("table.amount")}</Th>
							<Th>{t("table.custodian")}</Th>
							<Th>{t("table.description")}</Th>
							<Th>{t("table.updatedDate")}</Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{property.securityDeposits?.map((row, index) => (
							<SecurityDepositRow key={index} {...row} propertyId={property.id} />
						))}
						{property.securityDeposits.length === 0 && (
							<Tr>
								<Td colSpan={7}>{t("table.noData")}</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
		</Stack>
	);
}
